import React, { useState, useEffect } from "react";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import notificationService from "../../services/notificationService";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import "./TopHeader.css";

const TopHeader = ({ user }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { isSupported, isSubscribed, subscribe, unsubscribe, loading } = usePushNotifications();

  const handleTogglePush = async () => {
    if (!isSupported || loading) return;
    
    if (!isSubscribed) {
      await subscribe();
    } else {
      await unsubscribe();
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await notificationService.getNotifications();
      setNotifications(Array.isArray(data) ? data : (data.results || []));
      
      const countData = await notificationService.getUnreadCount();
      setUnreadCount(countData.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleShowDetail = (notification) => {
    setSelectedNotification(notification);
    setShowDetailModal(true);
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationService.clearAll();
      fetchNotifications();
    } catch (error) {
      console.error("Failed to clear notifications:", error);
    }
  };

  const getNotificationAction = (notification) => {
    if (!notification) return null;

    if (notification.type === "leave_requested") {
      return {
        label: "Go to Pending Approvals",
        url: notification.action_url || "/leave-approvals",
      };
    }

    if (
      notification.type === "leave_approved" ||
      notification.type === "leave_rejected" ||
      notification.type === "leave_cancelled"
    ) {
      return {
        label: "Go to My Leaves",
        url: notification.action_url || "/my-leaves",
      };
    }

    return null;
  };

  const handleNotificationAction = (notification) => {
    const action = getNotificationAction(notification);
    if (!action?.url) return;

    setShowDetailModal(false);

    const currentOrigin = window.location.origin;
    if (action.url.startsWith("http")) {
      const targetUrl = new URL(action.url, currentOrigin);
      if (targetUrl.origin === currentOrigin) {
        navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
        return;
      }
      window.location.href = action.url;
      return;
    }

    navigate(action.url);
  };

  return (
    <header className="top-header">
      <div className="header-left">
        {/* Placeholder for breadcrumbs or page title if needed */}
      </div>

        <div className="header-actions">
          {isSupported && (
            <button 
              className={`header-icon-btn push-status-btn ${isSubscribed ? 'subscribed' : 'unsubscribed'} ${loading ? 'loading' : ''}`}
              onClick={handleTogglePush}
              title={isSubscribed ? "Desktop Notifications Active" : "Enable Desktop Notifications"}
              type="button"
            >
              <span className="material-symbols-rounded">
                campaign
              </span>
              {loading && <div className="spinner-border spinner-border-sm text-primary position-absolute" style={{ width: '1rem', height: '1rem' }}></div>}
            </button>
          )}

          <Dropdown align="end" className="notification-dropdown">
            <Dropdown.Toggle as="div" className="header-icon-btn notification-toggle">
              <span className="material-symbols-rounded">notifications</span>
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </Dropdown.Toggle>

          <Dropdown.Menu className="notification-menu shadow-premium">
            <div className="notification-menu-header">
              <h6 className="mb-0">Notifications ({unreadCount})</h6>
              <div className="d-flex align-items-center gap-2">
                {unreadCount > 0 && (
                  <button className="btn-link-action" onClick={handleMarkAllAsRead}>
                    Mark all as read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button className="btn-link-action text-danger ms-2" onClick={handleClearAll}>
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="notification-menu-body">
              {notifications.length === 0 ? (
                <div className="notification-empty-state">
                  <div className="empty-state-bell">
                    <span className="material-symbols-rounded bell-icon">notifications</span>
                    <div className="check-badge">
                      <span className="material-symbols-rounded">check</span>
                    </div>
                  </div>
                  <p className="empty-text">You are up to date!</p>
                </div>
              ) : (
                <div className="notification-list">
                  {notifications.slice(0, 5).map((n) => (
                    <div 
                      key={n.id} 
                      className={`notification-menu-item ${!n.is_read ? 'unread' : ''}`} 
                      onClick={() => handleShowDetail(n)}
                    >
                      <div className="notification-item-icon">
                        <span className="material-symbols-rounded">
                          {n.type?.includes('asset') ? 'laptop' : 
                           n.type?.includes('leave') ? 'calendar_month' : 'notifications'}
                        </span>
                      </div>
                      <div className="notification-item-content">
                        <p className="item-title">{n.title}</p>
                        <p className="item-message text-truncate">{n.message}</p>
                        <p className="item-time">
                          {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="notification-menu-footer d-flex justify-content-between align-items-center">
              <button className="see-all-btn">See all notifications</button>
              {notifications.length > 0 && (
                <button className="btn-link-action text-danger p-0" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Notification Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        size="md"
        dialogClassName="ethereal-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              Notification Details
            </Modal.Title>
            <p className="myleaves-subtitle mt-2 mb-0">
              
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 pt-3 pb-2">
          {selectedNotification && (
            (() => {
              const notificationAction = getNotificationAction(selectedNotification);
              return (
            <div className="notification-detail-view mt-4">
              <div className="detail-card-premium mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className={`notification-badge-type ${selectedNotification.type}`} style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {selectedNotification.type?.replace(/_/g, ' ')}
                  </div>
                  <div className="text-muted small fw-bold">
                    {new Date(selectedNotification.created_at).toLocaleString()}
                  </div>
                </div>
                <h4 className="fw-800 text-dark mb-3" style={{ letterSpacing: '-0.02em' }}>{selectedNotification.title}</h4>
                <div className="reason-box-premium">
                  {selectedNotification.message}
                </div>
              </div>
              
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button 
                  variant="outline-secondary" 
                  className="rounded-pill px-4 fw-bold"
                  style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </Button>
                {notificationAction && (
                  <Button 
                    variant="primary" 
                    className="myleaves-new-btn px-4"
                    onClick={() => handleNotificationAction(selectedNotification)}
                  >
                    {notificationAction.label}
                  </Button>
                )}
              </div>
            </div>
              );
            })()
          )}
        </Modal.Body>
      </Modal>
    </header>
  );
};

export default TopHeader;
